// frontend/components/FormTList.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  Preview as PreviewIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import FormTPreview from './FormTPreview';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FormTList = ({ userRole, currentEmployeeId, companyId }) => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApp, setSelectedApp] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState({
    applicant_name: '',
    applicant_address: '',
    relationship: ''
  });
  const [resubmitting, setResubmitting] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    fetchApplications();
    if (userRole === 'hr') {
      fetchEmployees();
    }
  }, []);

  useEffect(() => {
    filterApplications();
  }, [statusFilter, employeeFilter, applications]);

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/form-t/employees/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees);
    } catch (error) {
      //console.error('Error fetching employees:', error);
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/form-t/my-applications?`;
      
      if (userRole === 'employee') {
        url += `employee_id=${currentEmployeeId}`;
      } else if (userRole === 'hr') {
        url += `company_id=${companyId}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApplications(response.data.applications);
      setFilteredApplications(response.data.applications);
      setLoading(false);
    } catch (error) {
      //console.error('Error fetching applications:', error);
      setError('Failed to fetch applications');
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    if (employeeFilter !== 'all' && userRole === 'hr') {
      filtered = filtered.filter(app => app.employee_id === employeeFilter);
    }
    
    setFilteredApplications(filtered);
  };

  const handleDownload = async (id, applicationNo) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/api/form-t/${id}/download?employee_id=${currentEmployeeId}`, {
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/pdf'
      },
      responseType: 'blob'
    });
    
    // Create blob URL
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    
    // Create temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    // Use application number for filename
const sanitizedAppNo = applicationNo.replace(/\//g, '_');
link.setAttribute('download', `Form_T_${sanitizedAppNo}.pdf`);
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
  } catch (error) {
    //console.error('Error downloading Form T:', error);
    if (error.response && error.response.data) {
      const reader = new FileReader();
      reader.onload = function() {
        try {
          const errorData = JSON.parse(reader.result);
          alert(errorData.error || 'Failed to download Form T');
        } catch (e) {
          alert('Failed to download Form T');
        }
      };
      reader.readAsText(error.response.data);
    } else {
      alert(error.response?.data?.error || 'Failed to download Form T');
    }
  }
};

  const handlePreview = async (app) => {
    const previewDataObj = {
      application_no: app.application_no,
      application_date: app.application_date,
      applicant_type: app.applicant_type,
      applicant_name: app.applicant_name,
      applicant_address: app.applicant_address,
      relationship: app.relationship,
      employee_name: app.employee ? `${app.employee.firstName} ${app.employee?.lastName || ""}` : 'N/A',
      employee_id: app.employee_id,
      employer_name: app.company?.companyname || 'Company Name',
      employer_address: app.company?.address || 'Company Address',
      gratuity_amount: app.gratuity_amount,
      notice_date: app.notice_date,
      status: app.status,
place: app.applicant_address
  ?.split(',')
  ?.slice(-2, -1)[0]
  ?.trim(),
        applicant_description: app.applicant_type === 'employee' ? 
        'an employee' : 
        app.applicant_type === 'nominee' ? 
        `a nominee of late ${app.employee?.firstName || 'the employee'}` : 
        `a legal heir of late ${app.employee?.firstName || 'the employee'}`
    };
    
    setPreviewData(previewDataObj);
    setSelectedApp(app);
    setPreviewOpen(true);
  };

  const handleEdit = (app) => {
    setSelectedApp(app);
    setEditData({
      applicant_name: app.applicant_name,
      applicant_address: app.applicant_address,
      relationship: app.relationship
    });
    setEditDialogOpen(true);
  };

  const handleResubmit = async () => {
    setResubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/form-t/${selectedApp.id}/resubmit?employee_id=${currentEmployeeId}`,
        editData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Application resubmitted successfully!');
        setEditDialogOpen(false);
        fetchApplications();
      }
    } catch (error) {
      //console.error('Error resubmitting:', error);
      alert(error.response?.data?.error || 'Failed to resubmit');
    } finally {
      setResubmitting(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      DRAFT: { label: 'Draft', color: 'default' },
      PENDING: { label: 'Pending', color: 'warning' },
      UNDER_REVIEW: { label: 'Under Review', color: 'info' },
      APPROVED: { label: 'Approved', color: 'success' },
      REJECTED: { label: 'Rejected', color: 'error' },
      RECOVERED: { label: 'Recovered', color: 'primary' }
    };
    const config = statusConfig[status] || { label: status, color: 'default' };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Filters for HR */}
      {userRole === 'hr' && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                  <MenuItem value="UNDER_REVIEW">Under Review</MenuItem>
                  <MenuItem value="APPROVED">Approved</MenuItem>
                  <MenuItem value="REJECTED">Rejected</MenuItem>
                  <MenuItem value="RECOVERED">Recovered</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee Filter</InputLabel>
                <Select
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  label="Employee Filter"
                >
                  <MenuItem value="all">All Employees</MenuItem>
                  {employees.map(emp => (
                    <MenuItem key={emp.employee_id} value={emp.employee_id}>
                      {emp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Typography variant="caption" color="text.secondary">
                Total: {filteredApplications.length} applications
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {filteredApplications.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No applications found.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click on "New Application" to submit Form T.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.100' }}>
              <TableRow>
                <TableCell><strong>Application No.</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                {userRole === 'hr' && <TableCell><strong>Employee</strong></TableCell>}
                <TableCell><strong>Applicant Type</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredApplications.map((app) => (
                <TableRow key={app.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {app.application_no}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(app.application_date).toLocaleDateString()}
                  </TableCell>
                  {userRole === 'hr' && (
                    <TableCell>
                      {app.employee?.firstName} {app.employee?.lastName}
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip 
                      label={app.applicant_type.replace('_', ' ')} 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    ₹ {app.gratuity_amount?.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {getStatusChip(app.status)}
                    {app.status === 'REJECTED' && app.rejection_reason && (
                      <Typography variant="caption" color="error" display="block">
                        Reason: {app.rejection_reason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Preview Form T">
                      <IconButton size="small" onClick={() => handlePreview(app)}>
                        <PreviewIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {app.status === 'APPROVED' && (
                      <Tooltip title="Download Form T">
                        <IconButton size="small" color="success" onClick={() => handleDownload(app.id, app.application_no)}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {app.status === 'REJECTED' && userRole === 'employee' && (
                      <Tooltip title="Edit and Resubmit">
                        <IconButton size="small" color="warning" onClick={() => handleEdit(app)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Preview Dialog */}
      <FormTPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        formData={previewData}
        onEdit={() => {
          setPreviewOpen(false);
          if (selectedApp) handleEdit(selectedApp);
        }}
        onDownload={() => {
          if (selectedApp) handleDownload(selectedApp.id, selectedApp.application_no);
        }}
        readOnly={previewData?.status !== 'REJECTED'}
      />

      {/* Edit Dialog for Rejected Applications */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit and Resubmit Application
          <IconButton
            aria-label="close"
            onClick={() => setEditDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedApp && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Please correct the following details and resubmit your application.
              </Alert>
              
              <TextField
                fullWidth
                label="Applicant Name"
                value={editData.applicant_name}
                onChange={(e) => setEditData({...editData, applicant_name: e.target.value})}
                margin="normal"
                required
              />
              
              <TextField
                fullWidth
                label="Applicant Address"
                value={editData.applicant_address}
                onChange={(e) => setEditData({...editData, applicant_address: e.target.value})}
                multiline
                rows={3}
                margin="normal"
                required
              />
              
              <TextField
                fullWidth
                label="Relationship to Employee"
                value={editData.relationship}
                onChange={(e) => setEditData({...editData, relationship: e.target.value})}
                margin="normal"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleResubmit}
            disabled={resubmitting}
            startIcon={resubmitting ? <CircularProgress size={20} /> : <EditIcon />}
          >
            {resubmitting ? 'Resubmitting...' : 'Resubmit Application'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormTList;